<chr-button-icon
  #closeButton
  *transloco="let t"
  class="close-button"
  icon="x"
  palette="neutral"
  variant="invisible"
  [attr.aria-label]="t('NAVIGATION.ARIA.BUTTONS.CLOSE_MENU')"
  (chr-action)="toggleVisibility()"
/>

<chr-side-nav
  *transloco="let t"
  [auto-collapse]="true"
  [auto-scroll]="true"
  [full-width]="handset()"
  [logo]="handset()"
  [palette]="palette()"
  [uncollapsible]="handset()"
>
  @if (logo()) {
    <img [alt]="t('NAVIGATION.COMPANY_LOGO')" slot="logo" [src]="logo()" />
  }

  <chr-nav-item [label]="t('NAVIGATION.SKIP_TO_MAIN_CONTENT')" class="skip-to-main-content" (chr-action)="skipToMain()"></chr-nav-item>

  <chr-nav-group #searchNavGroup icon="magnifier" [label]="t('NAVIGATION.SEARCH')" slot="search" data-pendo-nav-item-type="search">
    <div *transloco="let t" class="search-input" role="menuitem" [attr.aria-label]="t('COMMON.ARIA.INPUTS.AUTOMATIC_SEARCH')">
      <chr-input
        [formControl]="searchControl"
        icon="magnifier"
        [placeholder]="t('COMMON.PLACEHOLDERS.SEARCH')"
        [attr.aria-roledescription]="'search'"
      />
    </div>

    @for (result of searchResults() | keyvalue: compareSearchResults; track result.key) {
      <span class="product-name">{{ result.key }}</span>

      @for (position of result.value; track position) {
        @if (position.url) {
          <chr-nav-item
            [ciphrNavigationLink]="position.url"
            [description]="position.description || ''"
            [label]="position.label"
            (chr-action)="switchToActiveNavGroup()"
          />
        }
      }
    } @empty {
      @if (searchQuery()) {
        <ciphr-no-results />
      }
    }
  </chr-nav-group>

  @for (product of products(); track product.application) {
    @if (positionsMap().get(product.application); as positions) {
      <chr-nav-group
        [icon]="getProductIcon(product.application)"
        [label]="product.fullName"
        [short-label]="product.shortName"
        data-pendo-nav-item-type="product"
        [attr.data-pendo-nav-product-id]="product.application"
        [attr.data-test-id]="'navigationItem' + product.application"
      >
        <ng-container *ngTemplateOutlet="navigationPositions; context: { $implicit: positions }" />

        <ng-template #navigationPositions let-positions>
          @for (position of positions; track position) {
            @if (position.url) {
              <chr-nav-item
                [ciphrNavigationLink]="position.url"
                #link="ciphrNavigationLink"
                [label]="position.label"
                [selected]="link.active"
                [target]="position.target || '_self'"
                [attr.aria-current]="link.active"
              />
            } @else if (position.items) {
              <chr-nav-tree [label]="position.label">
                <ng-container *ngTemplateOutlet="navigationPositions; context: { $implicit: position.items }" />
              </chr-nav-tree>
            }

            @if (position.type === navigationItemTypes.Divider) {
              <hr />
            }
          }
        </ng-template>
      </chr-nav-group>
    } @else {
      <chr-nav-group
        [disabled]="!product.url"
        [href]="product.url || ''"
        [icon]="getProductIcon(product.application)"
        [label]="product.fullName"
        [short-label]="product.shortName"
        [target]="product.target || '_self'"
        data-pendo-nav-item-type="product"
        [attr.data-pendo-nav-product-id]="product.application"
        [attr.data-test-id]="'navigationItem' + product.application"
      />
    }
  }

  <!-- Render unpurchased items with a divider -->
  @if (!mobileMode() && getUnpucharsedItems().length > 0) {
    <div class="unpurchased-divider"></div>

    <span class="unpurchased-text">{{ t("NAVIGATION.AVAILABLE_UPGRADES") }}</span>

    @for (uProduct of getUnpucharsedItems(); track uProduct.application) {
      <chr-nav-group
        [disabled]="!uProduct.url"
        [href]="uProduct.url || ''"
        [icon]="getProductIcon(uProduct.application)"
        [label]="uProduct.fullName"
        [short-label]="uProduct.shortName"
        [target]="uProduct.target || '_self'"
        data-pendo-nav-item-type="product"
        [attr.data-pendo-nav-product-id]="'unpurchased-' + uProduct.application"
      />
    }
  }

  <div slot="privacy">
    <ng-content select="[privacyToggle]" />
  </div>

  <chr-nav-group [label]="username()" slot="user" data-pendo-nav-item-type="profile-menu" data-test-id="profileMenu">
    <chr-avatar ciphrAvatar slot="icon" [value]="username()" />

    @if (languages.length > 1) {
      <chr-nav-tree [label]="activeLanguageLabel()" data-pendo-nav-item-type="language-selector">
        @for (language of languages; track language.id) {
          <chr-nav-item [icon]="getLanguageIcon(language.id)" [label]="language.label" (chr-action)="selectActiveLanguage(language.id)" />
        }
      </chr-nav-tree>
    }

    <chr-nav-item
      [ciphrNavigationLink]="authIssuer + '/profile'"
      #link="ciphrNavigationLink"
      icon="user-crossed"
      [label]="t('NAVIGATION.PROFILE')"
      [selected]="link.active"
      data-pendo-nav-item-type="page"
      data-pendo-nav-link="profile"
      data-test-id="personalProfile"
    />

    @if (isSsoAdmin()) {
      <chr-nav-item
        [ciphrNavigationLink]="authIssuer + '/admin'"
        #link="ciphrNavigationLink"
        icon="user-cog"
        [label]="t('NAVIGATION.ADMIN')"
        [selected]="link.active"
        data-pendo-nav-item-type="page"
        data-pendo-nav-link="admin"
        data-test-id="ssoAdmin"
      />
    }

    @if (desktopVersionUrl(); as url) {
      <chr-nav-item
        [href]="url"
        icon="monitor"
        [label]="t('NAVIGATION.DESKTOP')"
        data-pendo-nav-item-type="action"
        data-pendo-nav-action="desktop"
      />
    }

    <chr-nav-item
      icon="exit"
      [label]="t('NAVIGATION.LOG_OUT')"
      data-pendo-nav-item-type="action"
      data-pendo-nav-action="log-out"
      data-test-id="logOut"
      (chr-action)="logOut()"
    />
  </chr-nav-group>
</chr-side-nav>
